// frontend/src/components/PixCobranca.js
//
// Gerar Cobrança › Pix Santander (ETAPA 4b). Substitui o formulário genérico da página quando a modalidade é Pix.
// Fluxo: escolher o cliente Pix → plano, valor, jornada e data do 1º débito → confirmação → recorrência criada no Santander
// (POST /api/pix/recurrences) → tela com o QR, o código copia e cola e o link para enviar ao cliente.
// O servidor confere tudo de novo (permissão, plano e valor, recorrência em aberto) e nunca repete uma criação sozinho.

import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Search, Smartphone, AlertTriangle, Check, Copy, Link2, MessageCircle, X, RefreshCw } from 'lucide-react';
import qrcode from '../lib/qrcode';
import { getPixProducts, createPixRecurrence, getPixQr, createPixShare } from '../services/pixApi';
import { buildWhatsappMessage } from '../lib/pixWhatsapp';
import DateInputBR from './DateInputBR';
import './PixCobranca.css';

const JORNADAS = [
  { id: 3, label: 'Jornada 3 — QR + Pagamento Imediato', desc: 'Cobrança com pagamento inicial' },
  { id: 4, label: 'Jornada 4 — QR + Vencimento', desc: 'Cobrança com data de vencimento' },
];
const MIN_AMOUNT = 1;
const MAX_AMOUNT = 5000;
const MAX_START_DAYS = 120;
const STATUS_LABELS = { ACTIVE: 'Ativa', ATIVA: 'Ativa', PENDING: 'Pendente', PENDENTE: 'Pendente', CRIADA: 'Criada' };
const whatsappMessage = (name, rec, code, url) => buildWhatsappMessage({ name, description: rec.description, amount: rec.amount, code, link: url, automatic: true });

// ───────────────────────────── utilitários ─────────────────────────────

const onlyDigits = (value) => String(value == null ? '' : value).replace(/\D/g, '');
const brl = (n) => Number(n).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' }).replace(/\u00a0/g, ' ');
const dateBr = (iso) => (iso ? String(iso).slice(0, 10).split('-').reverse().join('/') : '—');
const clientName = (c) => String((c && (c.full_name || c.name)) || '');

function formatCpf(value) {
  const d = onlyDigits(value);
  return d.length === 11 ? `${d.slice(0, 3)}.${d.slice(3, 6)}.${d.slice(6, 9)}-${d.slice(9)}` : String(value || '');
}

/** AAAA-MM-DD no fuso do navegador. */
function localIso(date) {
  const pad = (n) => String(n).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}

/** Dia 7 do mês seguinte: o padrão do Pix antigo para o primeiro débito. */
const nextMonthDay7 = () => {
  const today = new Date();
  return localIso(new Date(today.getFullYear(), today.getMonth() + 1, 7));
};
const tomorrowIso = () => { const d = new Date(); d.setDate(d.getDate() + 1); return localIso(d); };
const maxStartIso = () => { const d = new Date(); d.setDate(d.getDate() + MAX_START_DAYS); return localIso(d); };

/** "89,90", "89.90" ou "1.234,56" → número com 2 casas (de R$ 1,00 a R$ 5.000,00) ou null. */
function parseUiAmount(text) {
  let s = String(text == null ? '' : text).replace(/R\$|\s/g, '');
  if (s.includes(',')) s = s.replace(/\./g, '').replace(',', '.');
  if (!/^\d+(\.\d{1,2})?$/.test(s)) return null;
  const n = Number(s);
  return n >= MIN_AMOUNT && n <= MAX_AMOUNT ? n : null;
}
const toInputAmount = (n) => Number(n).toFixed(2).replace('.', ',');

/** O cadastro do cliente tem o endereço que o Pix exige para as jornadas 3 e 4? */
const hasFullAddress = (c) => /^\d{8}$/.test(onlyDigits(c.zip_code)) && !!String(c.logradouro || '').trim() && !!String(c.numero || '').trim()
  && !!String(c.bairro || '').trim() && !!String(c.city || '').trim() && /^[A-Za-z]{2}$/.test(String(c.state || '').trim());

const makeKey = () => {
  try {
    const bytes = new Uint8Array(16);
    window.crypto.getRandomValues(bytes);
    return Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
  } catch (error) {
    return `k${Date.now().toString(36)}${Math.random().toString(36).slice(2, 12)}`;
  }
};

async function copyText(text) {
  try {
    if (navigator.clipboard && navigator.clipboard.writeText) {
      await navigator.clipboard.writeText(text);
      return true;
    }
  } catch (error) { /* tenta o método antigo */ }
  try {
    const area = document.createElement('textarea');
    area.value = text;
    area.setAttribute('readonly', '');
    area.style.position = 'fixed';
    area.style.opacity = '0';
    document.body.appendChild(area);
    area.select();
    const ok = document.execCommand('copy');
    document.body.removeChild(area);
    return ok;
  } catch (error) {
    return false;
  }
}

/** Texto para o usuário a partir do erro de uma chamada. */
function describeError(error) {
  switch (error && error.code) {
    case 'PIX_ACCESS_DENIED':
      return 'Seu usuário não tem permissão para criar cobrança Pix. Peça a um administrador a permissão "Gerar Cobrança" e a modalidade Pix.';
    case 'IN_PROGRESS':
      return 'Já existe uma criação em andamento para este cliente. Aguarde um instante e confira em Cobranças → Pix antes de tentar de novo.';
    case 'PIX_REJECTED':
      return `O Pix recusou a criação: ${error.message}`;
    case 'PIX_NOT_CONFIGURED':
      return 'A integração com o Pix ainda não está configurada no servidor. Avise o administrador.';
    case 'PIX_LOGIN_FAILED':
    case 'PIX_AUTH_REJECTED':
      return 'O Pix recusou a integração. Avise o administrador.';
    case 'PIX_UNREACHABLE':
    case 'PIX_TIMEOUT':
      return 'O Pix não respondeu. Nada foi criado; tente de novo em instantes.';
    case 'PIX_PUBLIC_BASE_MISSING':
      return 'O endereço público do link do QR não está configurado no servidor. Avise o administrador.';
    case 'QR_NOT_READY':
      return 'O QR Code ainda não está disponível. Aguarde alguns segundos e tente de novo.';
    default:
      return (error && error.message) || 'Não foi possível concluir. Tente novamente.';
  }
}

/** QR em imagem a partir do código copia e cola (gerado aqui, no navegador: o código não sai da tela). */
function QrImage({ text }) {
  const source = useMemo(() => {
    try {
      const qr = qrcode(0, 'M');
      qr.addData(text);
      qr.make();
      return qr.createDataURL(6, 8);
    } catch (error) {
      return null;
    }
  }, [text]);
  if (!source) return <p className="pixc-erro">Não consegui desenhar o QR. Use o código copia e cola.</p>;
  return <img className="pixc-qr" src={source} alt="QR Code Pix" />;
}

// ───────────────────────────── componente ─────────────────────────────

/**
 * @param {Object} props
 * @param {Function} props.apiFetch fetch autenticado do Souli (a mesma da página).
 * @param {Object} [props.initialClient] cliente vindo do cadastro ("Salvar e Gerar Cobrança").
 * @param {Function} [props.onOpenList] abre a lista de cobranças Pix.
 * @param {number} [props.pollMs] intervalo da consulta do QR (padrão 3 s).
 * @param {number} [props.pollMax] máximo de consultas do QR (padrão 20).
 */
export default function PixCobranca({ apiFetch, initialClient = null, onOpenList, pollMs = 3000, pollMax = 20 }) {
  const startClient = initialClient && initialClient.id && (initialClient.payment_type || initialClient.type) === 'pix' ? initialClient : null;
  const [client, setClient] = useState(startClient);
  const [query, setQuery] = useState('');
  const [results, setResults] = useState(null);
  const [searching, setSearching] = useState(false);
  const [searchError, setSearchError] = useState('');

  const [products, setProducts] = useState([]);
  const [productsState, setProductsState] = useState('loading'); // loading | ok | error
  const [productsError, setProductsError] = useState('');
  const [productId, setProductId] = useState('');
  const [amountText, setAmountText] = useState('');
  const [jornada, setJornada] = useState(3);
  const [startDate, setStartDate] = useState(nextMonthDay7());
  const [errors, setErrors] = useState({});

  const [confirmOpen, setConfirmOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [apiError, setApiError] = useState('');
  const [existing, setExisting] = useState(null);
  const [stage, setStage] = useState('form'); // form | result
  const [result, setResult] = useState(null);
  const [qr, setQr] = useState(null);
  const [qrTimedOut, setQrTimedOut] = useState(false);
  const [pollRound, setPollRound] = useState(0);
  const [share, setShare] = useState(null); // { url, expiresAt }
  const [sharing, setSharing] = useState(false);
  const [shareError, setShareError] = useState('');
  const [copied, setCopied] = useState('');

  const keyRef = useRef(makeKey());
  const submittingRef = useRef(false);
  const copiedTimer = useRef(null);

  const product = products.find((p) => p.id === productId) || null;
  const amountNumber = parseUiAmount(amountText);
  const amountChanged = !!product && amountNumber !== null && Math.abs(amountNumber - product.amount) > 0.004;

  useEffect(() => () => { if (copiedTimer.current) clearTimeout(copiedTimer.current); }, []);

  const selectProduct = (id, list = products) => {
    setProductId(id);
    const chosen = list.find((p) => p.id === id);
    setAmountText(chosen ? toInputAmount(chosen.amount) : '');
    setErrors((previous) => ({ ...previous, productId: undefined, amount: undefined }));
  };

  const loadProducts = async () => {
    setProductsState('loading');
    setProductsError('');
    try {
      const list = await getPixProducts();
      setProducts(list);
      setProductsState('ok');
      if (list.length === 1) selectProduct(list[0].id, list);
    } catch (error) {
      setProductsError(describeError(error));
      setProductsState('error');
    }
  };
  useEffect(() => { loadProducts(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ── busca do cliente ──
  const searchClients = async () => {
    const text = query.trim();
    if (text.length < 2) { setSearchError('Digite ao menos 2 letras do nome ou o CPF.'); return; }
    setSearching(true);
    setSearchError('');
    try {
      const response = await apiFetch(`/clients?payment_type=pix&search=${encodeURIComponent(text)}&limit=10`);
      const raw = (response && (response.data || response.clients)) || (Array.isArray(response) ? response : []);
      setResults(raw.filter((c) => c && c.id && c.cpf && (!c.payment_type || c.payment_type === 'pix')));
    } catch (error) {
      setResults(null);
      setSearchError('Não foi possível buscar agora. Tente novamente.');
    } finally {
      setSearching(false);
    }
  };
  const pickClient = (c) => { setClient(c); setResults(null); setQuery(''); setErrors((previous) => ({ ...previous, client: undefined })); setApiError(''); setExisting(null); };

  // ── enviar ──
  const validate = () => {
    const found = {};
    if (!client) found.client = 'Busque e selecione um cliente.';
    else if (!hasFullAddress(client)) found.client = 'O cadastro deste cliente não tem endereço completo (CEP, logradouro, número, bairro, cidade e UF). Complete o cadastro antes de cobrar.';
    if (!productId) found.productId = 'Escolha o plano.';
    if (amountNumber === null) found.amount = 'Valor inválido (de R$ 1,00 a R$ 5.000,00).';
    if (!startDate) found.startDate = 'Informe a data do primeiro débito.';
    else if (!/^\d{4}-\d{2}-\d{2}$/.test(startDate)) found.startDate = 'Data inválida. Use o formato dd/mm/aaaa.';
    else if (startDate < tomorrowIso()) found.startDate = 'A data do primeiro débito deve ser a partir de amanhã.';
    else if (startDate > maxStartIso()) found.startDate = `A data do primeiro débito pode ser no máximo daqui a ${MAX_START_DAYS} dias.`;
    return found;
  };

  const goNext = (event) => {
    if (event) event.preventDefault();
    const found = validate();
    setErrors(found);
    setApiError('');
    if (Object.keys(found).length) return;
    setConfirmOpen(true);
  };

  const submit = async (confirmExisting = false) => {
    if (submittingRef.current) return; // clique duplo: só uma criação por vez
    submittingRef.current = true;
    setSaving(true);
    setApiError('');
    try {
      const data = await createPixRecurrence({
        clientId: client.id,
        productId,
        jornada,
        startDate,
        idempotencyKey: keyRef.current,
        ...(amountChanged && { amount: amountNumber }),
        ...(confirmExisting && { confirmExisting: true }),
      });
      setResult(data);
      setQr(data.qr || null);
      setQrTimedOut(false);
      setShare(null);
      setShareError('');
      setExisting(null);
      setConfirmOpen(false);
      setStage('result');
    } catch (error) {
      if (error && error.code === 'OPEN_RECURRENCE_EXISTS') {
        setExisting((error.data && error.data.existing) || []);
        setConfirmOpen(false);
      } else {
        if (error && error.fields) setErrors(error.fields);
        setApiError(describeError(error));
      }
    } finally {
      submittingRef.current = false;
      setSaving(false);
    }
  };

  // ── consulta do QR enquanto ele não fica pronto ──
  useEffect(() => {
    if (stage !== 'result' || !result || qr) return undefined;
    let cancelled = false;
    let tries = 0;
    let timer = null;
    const tick = async () => {
      if (cancelled) return;
      tries += 1;
      try {
        const answer = await getPixQr(result.recurrence.id);
        if (cancelled) return;
        if (answer && answer.ready && answer.qr) { setQr(answer.qr); return; }
      } catch (error) { /* tenta de novo */ }
      if (cancelled) return;
      if (tries >= pollMax) { setQrTimedOut(true); return; }
      timer = setTimeout(tick, pollMs);
    };
    timer = setTimeout(tick, pollMs);
    return () => { cancelled = true; clearTimeout(timer); };
  }, [stage, result, qr, pollMs, pollMax, pollRound]);

  const retryQr = () => { setQrTimedOut(false); setPollRound((n) => n + 1); };

  const doCopy = async (what, text) => {
    const ok = await copyText(text);
    setCopied(ok ? what : `${what}-falhou`);
    if (copiedTimer.current) clearTimeout(copiedTimer.current);
    copiedTimer.current = setTimeout(() => setCopied(''), 2500);
  };

  const makeShare = async () => {
    setSharing(true);
    setShareError('');
    try {
      const answer = await createPixShare(result.recurrence.id);
      if (!answer || !/^https?:\/\//i.test(String(answer.url))) throw new Error('O Pix não devolveu um link válido.');
      setShare({ url: answer.url, expiresAt: answer.expiresAt });
    } catch (error) {
      setShareError(describeError(error));
    } finally {
      setSharing(false);
    }
  };

  const resetAll = () => {
    keyRef.current = makeKey();
    setClient(null); setQuery(''); setResults(null); setErrors({}); setApiError(''); setExisting(null);
    setStartDate(nextMonthDay7()); setJornada(3); setResult(null); setQr(null); setQrTimedOut(false); setShare(null); setShareError(''); setCopied('');
    if (products.length === 1) selectProduct(products[0].id); else { setProductId(''); setAmountText(''); }
    setStage('form');
  };

  const closeConfirm = () => { if (!saving) { setConfirmOpen(false); setApiError(''); } };
  const jornadaInfo = JORNADAS.find((j) => j.id === Number(jornada));

  // ───────────── tela: resultado ─────────────
  if (stage === 'result' && result) {
    const rec = result.recurrence;
    const phone = (() => { let d = onlyDigits(client && client.phone); if ((d.length === 12 || d.length === 13) && d.startsWith('55')) d = d.slice(2); return /^\d{10,11}$/.test(d) ? d : null; })();
    const whatsappUrl = share && phone ? `https://wa.me/55${phone}?text=${encodeURIComponent(whatsappMessage(clientName(client), rec, qr && qr.copiaECola, share.url))}` : null;
    return (
      <div className="pixc">
        <div className="pixc-sucesso"><Check size={20} /> <strong>Recorrência criada</strong></div>
        {result.recovered && <div className="pixc-aviso" role="status"><AlertTriangle size={16} /> A conexão caiu durante a criação, mas confirmei no Pix que a recorrência foi criada (uma só).</div>}
        {result.warning && <div className="pixc-aviso" role="alert"><AlertTriangle size={16} /> O Pix avisou: {result.warning.message} A recorrência foi criada, mas confira a cobrança inicial em Cobranças → Pix antes de enviar o QR ao cliente.</div>}
        <dl className="pixc-resumo">
          <div><dt>Cliente</dt><dd>{clientName(client)}</dd></div>
          <div><dt>Plano</dt><dd>{rec.description}</dd></div>
          <div><dt>Valor</dt><dd>{brl(rec.amount)}</dd></div>
          <div><dt>Jornada</dt><dd>{rec.jornada}</dd></div>
          <div><dt>Primeiro débito</dt><dd>{dateBr(rec.startDate)}</dd></div>
          {rec.idRec && <div><dt>Código no Santander</dt><dd>{rec.idRec}</dd></div>}
        </dl>

        <h4 className="pixc-sub">QR Code para o cliente</h4>
        {qr ? (
          <div className="pixc-qrbloco">
            <QrImage text={qr.copiaECola} />
            <div className="pixc-qrlado">
              <label className="pixc-rotulo" htmlFor="pixc-codigo">Pix Copia e Cola</label>
              <textarea id="pixc-codigo" className="pixc-codigo" readOnly value={qr.copiaECola} rows={4} />
              <button type="button" className="pixc-btn pixc-btn-primario" onClick={() => doCopy('codigo', qr.copiaECola)}>
                <Copy size={16} /> {copied === 'codigo' ? 'Copiado!' : 'Copiar código Pix'}
              </button>
              {copied === 'codigo-falhou' && <p className="pixc-erro" role="alert">Não consegui copiar. Selecione o código acima e copie à mão.</p>}
            </div>
          </div>
        ) : qrTimedOut ? (
          <div className="pixc-aviso" role="alert"><AlertTriangle size={16} /> O QR ainda não ficou pronto. <button type="button" className="pixc-link" onClick={retryQr}><RefreshCw size={13} /> Tentar de novo</button></div>
        ) : (
          <p className="pixc-gerando" role="status"><span className="pixc-spinner" /> Gerando o QR Code...</p>
        )}

        <h4 className="pixc-sub">Link para enviar ao cliente</h4>
        {!share ? (
          <>
            <button type="button" className="pixc-btn" onClick={makeShare} disabled={!qr || sharing}><Link2 size={16} /> {sharing ? 'Gerando link...' : 'Gerar link para o cliente'}</button>
            {shareError && <p className="pixc-erro" role="alert">{shareError}</p>}
          </>
        ) : (
          <div className="pixc-link-bloco">
            <input className="pixc-input" readOnly value={share.url} aria-label="Link para o cliente" onFocus={(e) => e.target.select()} />
            <div className="pixc-acoes">
              <button type="button" className="pixc-btn" onClick={() => doCopy('link', share.url)}><Copy size={16} /> {copied === 'link' ? 'Copiado!' : 'Copiar link'}</button>
              {whatsappUrl && <a className="pixc-btn pixc-btn-whats" href={whatsappUrl} target="_blank" rel="noopener noreferrer"><MessageCircle size={16} /> Enviar por WhatsApp</a>}
            </div>
            {copied === 'link-falhou' && <p className="pixc-erro" role="alert">Não consegui copiar. Selecione o link acima e copie à mão.</p>}
            <p className="pixc-nota">Link válido até {dateBr(share.expiresAt)}. Quando o cliente autorizar, a página dele passa a mostrar "Autorização concluída".</p>
            {!phone && <p className="pixc-nota">Este cliente não tem telefone válido no cadastro: copie o link e envie por onde preferir.</p>}
          </div>
        )}

        <div className="pixc-rodape">
          <button type="button" className="pixc-btn" onClick={resetAll}>Nova cobrança</button>
          {onOpenList && <button type="button" className="pixc-btn" onClick={onOpenList}>Ver cobranças Pix</button>}
        </div>
      </div>
    );
  }

  // ───────────── tela: formulário ─────────────
  return (
    <div className="pixc">
      <h3 className="pixc-titulo"><Smartphone size={20} /> Gerar cobrança via Pix Santander</h3>

      <form onSubmit={goNext} noValidate>
        <section className="pixc-secao">
          <label className="pixc-rotulo" htmlFor="pixc-busca">1️⃣ Busque o cliente Pix *</label>
          {client ? (
            <div className="pixc-cliente">
              <div>
                <strong>{clientName(client)}</strong>
                <span className="pixc-sub-linha">CPF {formatCpf(client.cpf)}{client.plan_name ? ` · ${client.plan_name}` : ''}</span>
              </div>
              <button type="button" className="pixc-btn" onClick={() => { setClient(null); setExisting(null); }}>Trocar cliente</button>
            </div>
          ) : (
            <>
              <div className="pixc-busca">
                <input id="pixc-busca" className="pixc-input" type="text" value={query} placeholder="Digite nome ou CPF" onChange={(e) => setQuery(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); searchClients(); } }} />
                <button type="button" className="pixc-btn pixc-btn-primario" onClick={searchClients} disabled={searching}><Search size={16} /> {searching ? 'Buscando...' : 'Buscar'}</button>
              </div>
              {searchError && <p className="pixc-erro" role="alert">{searchError}</p>}
              {results && results.length === 0 && <p className="pixc-nota">Nenhum cliente Pix encontrado. Cadastre-o em Clientes → Cadastro de Clientes → Pix Santander.</p>}
              {results && results.length > 0 && (
                <ul className="pixc-lista">
                  {results.map((c) => (
                    <li key={c.id}>
                      <button type="button" className="pixc-item" onClick={() => pickClient(c)}>
                        <strong>{clientName(c)}</strong>
                        <span className="pixc-sub-linha">CPF {formatCpf(c.cpf)}{c.plan_name ? ` · ${c.plan_name}` : ''}</span>
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </>
          )}
          {errors.client && <p className="pixc-erro" role="alert">{errors.client}</p>}
        </section>

        <section className="pixc-grade">
          <div className="pixc-campo">
            <label className="pixc-rotulo" htmlFor="pixc-plano">Plano *</label>
            <select id="pixc-plano" className="pixc-input" value={productId} disabled={productsState !== 'ok'} onChange={(e) => selectProduct(e.target.value)}>
              <option value="">{productsState === 'loading' ? 'Carregando planos...' : 'Selecione'}</option>
              {products.map((p) => <option key={p.id} value={p.id}>{`${p.name} — ${brl(p.amount)}`}</option>)}
            </select>
            {productsState === 'error' && <p className="pixc-erro" role="alert">{productsError} <button type="button" className="pixc-link" onClick={loadProducts}>Tentar de novo</button></p>}
            {errors.productId && <p className="pixc-erro" role="alert">{errors.productId}</p>}
          </div>
          <div className="pixc-campo">
            <label className="pixc-rotulo" htmlFor="pixc-valor">Valor (R$) *</label>
            <input id="pixc-valor" className="pixc-input" inputMode="decimal" value={amountText} placeholder="0,00" onChange={(e) => { setAmountText(e.target.value); setErrors((p) => ({ ...p, amount: undefined })); }} />
            {amountChanged && <p className="pixc-nota pixc-nota-alerta" role="status">Valor alterado: o plano custa {brl(product.amount)}.</p>}
            {errors.amount && <p className="pixc-erro" role="alert">{errors.amount}</p>}
          </div>
          <div className="pixc-campo">
            <label className="pixc-rotulo" htmlFor="pixc-jornada">Jornada *</label>
            <select id="pixc-jornada" className="pixc-input" value={jornada} onChange={(e) => setJornada(Number(e.target.value))}>
              {JORNADAS.map((j) => <option key={j.id} value={j.id}>{j.label}</option>)}
            </select>
            {jornadaInfo && <p className="pixc-nota">{jornadaInfo.desc}</p>}
          </div>
          <div className="pixc-campo">
            <label className="pixc-rotulo" htmlFor="pixc-data">Data do primeiro débito *</label>
            <DateInputBR id="pixc-data" className="pixc-input" value={startDate} min={tomorrowIso()} max={maxStartIso()} onChange={(v) => { setStartDate(v); setErrors((p) => ({ ...p, startDate: undefined })); }} />
            {errors.startDate && <p className="pixc-erro" role="alert">{errors.startDate}</p>}
          </div>
          <div className="pixc-campo pixc-campo-largo">
            <label className="pixc-rotulo" htmlFor="pixc-descricao">Descrição</label>
            <input id="pixc-descricao" className="pixc-input" readOnly value={product ? product.name.slice(0, 35) : ''} placeholder="Vem do plano" />
          </div>
        </section>

        {existing && (
          <div className="pixc-aviso pixc-aviso-forte" role="alert">
            <AlertTriangle size={18} />
            <div>
              <strong>Este cliente já tem recorrência em aberto no Pix.</strong>
              <p>Criar outra pode gerar cobrança em duplicidade.</p>
              <ul className="pixc-existentes">
                {existing.map((r) => <li key={r.id}>{STATUS_LABELS[String(r.status).toUpperCase()] || r.status} · {brl(r.amount)} · jornada {r.jornada} · início {dateBr(r.startDate)}</li>)}
              </ul>
              <div className="pixc-acoes">
                <button type="button" className="pixc-btn" onClick={() => setExisting(null)}>Cancelar</button>
                <button type="button" className="pixc-btn pixc-btn-perigo" onClick={() => submit(true)} disabled={saving}>{saving ? 'Criando...' : 'Criar outra mesmo assim'}</button>
              </div>
            </div>
          </div>
        )}
        {apiError && !confirmOpen && <p className="pixc-erro" role="alert">{apiError}</p>}

        <div className="pixc-rodape">
          <button type="submit" className="pixc-btn pixc-btn-primario pixc-btn-grande" disabled={productsState !== 'ok'}>Próximo →</button>
        </div>
      </form>

      {confirmOpen && (
        <div className="pixc-fundo" onClick={closeConfirm}>
          <div className="pixc-modal" role="dialog" aria-modal="true" aria-labelledby="pixc-modal-titulo" onClick={(e) => e.stopPropagation()} onKeyDown={(e) => { if (e.key === 'Escape') closeConfirm(); }}>
            <div className="pixc-modal-topo">
              <h3 id="pixc-modal-titulo">Confirmar cobrança Pix</h3>
              <button type="button" className="pixc-fechar" onClick={closeConfirm} aria-label="Fechar" disabled={saving}><X size={18} /></button>
            </div>
            <dl className="pixc-resumo">
              <div><dt>Cliente</dt><dd>{clientName(client)}</dd></div>
              <div><dt>CPF</dt><dd>{formatCpf(client.cpf)}</dd></div>
              <div><dt>Plano</dt><dd>{product ? product.name : ''}</dd></div>
              <div><dt>Valor mensal</dt><dd className="pixc-valor">{brl(amountNumber)}{amountChanged ? <small> (alterado de {brl(product.amount)})</small> : null}</dd></div>
              <div><dt>Jornada</dt><dd>{jornadaInfo ? jornadaInfo.label : jornada}</dd></div>
              <div><dt>Primeiro débito</dt><dd>{dateBr(startDate)}</dd></div>
            </dl>
            <p className="pixc-aviso"><AlertTriangle size={16} /> Isto cria uma recorrência <strong>real</strong> no Santander. Depois de criada, só dá para cancelar pelo Pix.</p>
            {apiError && <p className="pixc-erro" role="alert">{apiError}</p>}
            <div className="pixc-acoes pixc-acoes-fim">
              <button type="button" className="pixc-btn" onClick={closeConfirm} disabled={saving}>Voltar</button>
              <button type="button" className="pixc-btn pixc-btn-primario" onClick={() => submit(false)} disabled={saving}>{saving ? 'Criando...' : 'Criar recorrência'}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
